import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAnnouncement,
  getAnnouncements,
} from "../../api/announcements";
import AttachmentList from "./AttachmentList";
import { useNotifications } from "../../context/NotificationsContext";
import {
  getAnnouncementNotificationIds,
  getUnreadAnnouncementIds,
} from "../../utils/notificationTarget";
import "./AnnouncementsInbox.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

function targetLabel(item) {
  if (item.target === "ALL") return "تعميم عام";
  if (item.target === "DEPARTMENT") {
    return item.department?.name ? `إدارة ${item.department.name}` : "إدارة";
  }
  if (item.target === "BRANCH") {
    return item.branch?.name ? `فرع ${item.branch.name}` : "فرع";
  }
  if (item.target === "EMPLOYEES") return "إعلان موجه";
  return "";
}

function AnnouncementsInbox({ initialAnnouncementId = null }) {
  const { notifications, markAsRead } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(initialAnnouncementId);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const unreadIds = useMemo(
    () => getUnreadAnnouncementIds(notifications),
    [notifications],
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAnnouncements({ status: "PUBLISHED" });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل الإعلانات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (initialAnnouncementId) {
      setSelectedId(initialAnnouncementId);
    }
  }, [initialAnnouncementId]);

  useEffect(() => {
    if (!selectedId) return;
    const ids = getAnnouncementNotificationIds(notifications, selectedId);
    ids.forEach((id) => {
      const notif = notifications.find((n) => String(n.id) === String(id));
      if (notif && !(notif.isRead ?? notif.read)) {
        markAsRead(id).catch(() => {});
      }
    });
  }, [selectedId, notifications, markAsRead]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }

    let cancelled = false;
    const fromList = items.find((item) => item.id === selectedId);
    if (fromList?.body) {
      setSelected(fromList);
    }

    setDetailLoading(true);
    getAnnouncement(selectedId)
      .then((data) => {
        if (!cancelled) setSelected(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, "تعذر فتح الإعلان"));
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId, items]);

  return (
    <div className="announcements-inbox" dir="rtl">
      <div className="announcements-inbox-header">
        <h1 className="announcements-inbox-title">الإعلانات</h1>
        <p className="announcements-inbox-subtitle">
          الإعلانات المهمة الصادرة من الموارد البشرية
        </p>
      </div>

      {error ? (
        <div className="announcements-inbox-alert">{error}</div>
      ) : null}

      {selectedId ? (
        <div className="announcements-inbox-detail">
          <button
            type="button"
            className="announcements-inbox-back"
            onClick={() => setSelectedId(null)}
          >
            رجوع للقائمة
          </button>
          {detailLoading && !selected ? (
            <div className="announcements-inbox-empty">جاري التحميل...</div>
          ) : selected ? (
            <article className="announcements-inbox-card is-detail">
              <div className="announcements-inbox-card-top">
                <h2>{selected.title}</h2>
                <span className="announcements-inbox-chip">
                  {targetLabel(selected)}
                </span>
              </div>
              <div className="announcements-inbox-meta">
                {formatDateTime(selected.publishedAt || selected.createdAt)}
              </div>
              {selected.summary ? (
                <p className="announcements-inbox-summary">{selected.summary}</p>
              ) : null}
              <p className="announcements-inbox-body">{selected.body}</p>
              <AttachmentList
                entityType="announcement"
                entityId={selected.id}
                showDelete={false}
                hideIfEmpty
              />
            </article>
          ) : null}
        </div>
      ) : loading ? (
        <div className="announcements-inbox-empty">جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div className="announcements-inbox-empty">لا توجد إعلانات حالياً</div>
      ) : (
        <div className="announcements-inbox-list">
          {items.map((item) => {
            const isUnread = unreadIds.has(String(item.id));
            return (
              <button
                key={item.id}
                type="button"
                className={`announcements-inbox-card${isUnread ? " is-unread" : ""}`}
                onClick={() => setSelectedId(item.id)}
              >
                <div className="announcements-inbox-card-top">
                  <h2>
                    {item.title}
                    {isUnread ? (
                      <span className="announcements-inbox-new">جديد</span>
                    ) : null}
                  </h2>
                  <span className="announcements-inbox-chip">
                    {targetLabel(item)}
                  </span>
                </div>
                <div className="announcements-inbox-meta">
                  {formatDateTime(item.publishedAt || item.createdAt)}
                  {item.attachmentCount > 0 ? (
                    <span className="announcements-inbox-clip">مرفق</span>
                  ) : null}
                </div>
                <p className="announcements-inbox-preview">
                  {item.summary || item.body}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AnnouncementsInbox;
